import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Input } from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

const propTypes = {

	/**
	 * The input id. If none is provided, the input name is used.
	 */
	id: PropTypes.string,

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value.
	 */
	value: PropTypes.string,

	/**
	 * The input options (if any).
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string,

		label: PropTypes.string,

		value: PropTypes.string,

		weight: PropTypes.number,

	})),

	/**
	 * The input placeholder text.
	 */
	placeholder: PropTypes.string,

	/**
	 * The id of the feedback text
	 */
	feedbackID: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * True, if the values are language options and require the lang attribute
	 * for screen reader accessibility
	 */
	lang: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func

};

const SelectInput = (props) => {

	const {
		name,
		id,
		value,
		options,
		placeholder,
		feedbackID,
		isEditing,
		isInvalid,
		disabled,
		lang,
		onChange
	} = props;

	if (!isEditing) {

		if (!value) {

			return (
				<div>
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				</div>
			);

		}

		const matchingOption = options?.find((option) => option.value === value);

		return (
			<div className="tc-material-input-select-display-value">
				{matchingOption?.label || value}
			</div>
		);

	}

	// Sort the options!
	const sortedOptions = options?.sort((option1, option2) => {

		// Support for options that haven't been migrated to use weights yet!
		if (option1.weight === undefined ||
			option1.weight === null ||
			option2.weight === undefined ||
			option2.weight === null) {
			return 0;
		}

		return option1.weight - option2.weight;

	});

	const onInputChange = (event) => {

		const newValue = event.target.value;

		let selectedIndex = event.target.selectedIndex;

		// If a placeholder is provided, we need to subtract one from
		// selectedIndex in order to access the correct form field option
		if (placeholder) {
			selectedIndex -= 1;
		}

		const selectedOption = sortedOptions[selectedIndex];

		onChange(newValue, selectedOption.id);

	};

	return (
		<div className={`tc-material-input-select ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			<Input
				id={id || name}
				type="select"
				aria-describedby={isInvalid ? feedbackID : undefined}
				name={name}
				value={value || ''}
				invalid={isInvalid}
				disabled={disabled}
				onChange={onInputChange}>
				{placeholder &&
					<option
						value=""
						disabled>
						{placeholder}
					</option>
				}
				{sortedOptions?.map((option) => (
					<option
						key={option.value}
						lang={lang ? option.value : undefined}
						value={option.value}>
						{option.label || option.value}
					</option>
				))}
			</Input>
		</div>
	);
};

SelectInput.propTypes = propTypes;

export default SelectInput;
